import Lottie from 'lottie-react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { createSearchParams, useNavigate } from 'react-router-dom';
import fireHigh from '../../assets/lottie-animation/FireHigh.json';
import fireLow from '../../assets/lottie-animation/FireLow.json';
import fireMedium from '../../assets/lottie-animation/FireMedium.json';
import spinner from '../../assets/lottie-animation/Spinner.json';
import AMLRiskIndicator from '../../components/AMLRiskIndicator/AMLRiskIndicator';
import Button from '../../components/Button';
import Icon, { Icons } from '../../components/Icon/Icon';
import IconAML from '../../components/IconAML/IconAML';
import InlineNotice from '../../components/InlineNotice';
import ListItem from '../../components/ListItem';
import PageHeader from '../../components/PageHeader';
import StickyPanel from '../../components/StickyPanel';
import AppConfig from '../../config';
import { usePageContext } from '../../hooks/usePageContext';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import TypesService from '../../services/TypesService';
import AmlCheckProgress from '../AmlCheckProgress/AmlCheckProgress';
import './AMLCheckResult.scss';

const DURATION = 1200;
const DELAY = 0;
const PROGRESS_STATUSES = ['Pending', 'Aborted', 'Queued', 'Requested'];

const AMLCheckResult = () => {
	const navigate = useNavigate();
	const intl = useIntl();
	const { routes } = AppConfig;
	const { useSearchParams, useSelector } = usePageContext();

	const aml = useSelector(store => store.app.wsAmlResult);
	// const aml = { status: 'Canceled' };
	// const aml = { status: 'Failed' };
	// const aml = { status: 'Pending' };
	// const aml = { status: 'Aborted' };
	// const aml = { status: 'Queued' };
	// const aml = { status: 'Requested' };
	// const aml = { status: 'Success', riskSkore: null, checkDate: "2026-03-03T09:23:20", blockchain: 'Tron', address: 'TCBQMLUtZHMAKwDiLHLxZHmumVaocdoigA' } // Unverifired

	const params = useSearchParams();
	const expectedAddress = params.get('address');
	const expectedBlockchain = params.get('blockchain');
	const expectedOpenError = params.get('openError');
	const gotIt = params.get('gotIt');

	const [isLoadingPage, setIsLoadingPage] = useState(true);
	const [isLoadingAML, setIsLoadingAML] = useState(true);
	const [displayPercent, setDisplayPercent] = useState(null);

	const blockchainsById = TypesService.BlockchainsById;
	const blockchainData = Object.values(blockchainsById).find(item => item.Name === expectedBlockchain);

	const currentRisk = useMemo(
		() => HelperService.getAmlRiskByText(displayPercent, true, aml?.blockchain, aml?.error, aml?.checkDate).risk,
		[displayPercent, aml]
	);

	const riskForFireAnimation = useMemo(
		() => HelperService.getAmlRiskByText(aml?.riskScore, true, aml?.blockchain, aml?.error, aml?.checkDate).risk,
		[aml]
	);

	const clampPercentToRange = value => Math.max(0, Math.min(100, Number(value || 0)));

	const indicatorColor = useMemo(() => {
		if (currentRisk === 'Low') return 'rgba(var(--AML-low), 1)';
		if (currentRisk === 'Medium') return 'rgba(var(--AML-medium), 1)';
		if (currentRisk === 'High') return 'rgba(var(--AML-high), 1)';
		if (aml && (aml?.status === 'Failed' || aml?.status === 'Canceled')) return 'rgba(var(--AML-high), 1)';
		if (expectedOpenError) return 'rgba(var(--AML-high), 1)';
		if (currentRisk === 'Unverified') return 'rgba(var(--AML-outdated), 1)';

		return 'rgba(var(--AML-low), 1)';
	}, [currentRisk, aml, expectedOpenError]);

	const animationVariant = useMemo(() => {
		if (riskForFireAnimation === 'Low') return fireLow;
		if (riskForFireAnimation === 'Medium') return fireMedium;
		if (riskForFireAnimation === 'High') return fireHigh;

		return fireHigh;
	}, [riskForFireAnimation]);

	const formatAmlDate = isoString => {
		if (!isoString) return '';

		return isoString.replace('T', ' ').slice(0, 16);
	};

	const onMoreDetailsClick = () => {
		navigate({
			pathname: routes.addressAmlDetails,
			search: createSearchParams({ address: aml?.address }).toString(),
		});
	};

	const onTryAgainClick = () => {
		navigate({
			pathname: routes.amlCheck,
			search: createSearchParams({ address: expectedAddress }).toString(),
		});
	};

	const onGotItClick = () => {
		if (gotIt === 'toSendRecipient')
			return navigate({
				pathname: routes.sendRecipient,
				search: createSearchParams({ address: expectedAddress }).toString(),
			});

		navigate(-1);
	};

	useEffect(() => {
		if (!aml || isLoadingPage || PROGRESS_STATUSES.includes(aml?.status)) return;

		const timeoutId = window.setTimeout(() => {
			setIsLoadingAML(false);
		}, Math.max(0, Number(DURATION) || 0));

		return () => clearTimeout(timeoutId);
	}, [aml, isLoadingPage]);

	useEffect(() => {
		if (isLoadingPage || !aml || !aml?.riskScore) return;

		const targetPercent = clampPercentToRange(aml?.riskScore);
		setDisplayPercent(0);
		let rafId = 0;
		let timeoutId = 0;

		timeoutId = window.setTimeout(() => {
			const animationStartTime = performance.now();
			const tick = now => {
				const elapsed = now - animationStartTime;
				const progress = Math.min(1, elapsed / Math.max(1, DURATION));
				const easedProgress = 1 - Math.pow(1 - progress, 3);
				const nextDisplayedValue = targetPercent * easedProgress;
				setDisplayPercent(nextDisplayedValue);

				if (progress < 1) {
					rafId = requestAnimationFrame(tick);
				}
			};

			rafId = requestAnimationFrame(tick);
		}, Math.max(0, Number(DELAY) || 0));

		return () => {
			clearTimeout(timeoutId);
			cancelAnimationFrame(rafId);
		};
	}, [aml, isLoadingPage]);

	useEffect(() => {
		const timeoutId = window.setTimeout(() => setIsLoadingPage(false), 4000);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, []);

	if (isLoadingPage) {
		return <AmlCheckProgress />;
	}

	if ((aml && (aml?.status === 'Failed' || aml?.status === 'Canceled')) || expectedOpenError) {
		return (
			<div className='AMLCheckResult' style={{ '--colorVariable': indicatorColor }}>
				<StickyPanel
					controls={
						<Button onClick={onGotItClick} styleType='white'>
							{t('Got it')}
						</Button>
					}
				>
					<div className='AMLCheckResult_background' />
					<PageHeader collapsed={true} />

					{(aml?.status === 'Failed' || expectedOpenError) && (
						<div className='AMLCheckResult_content'>
							<div className='AMLCheckResult_content_error_icon'>
								<Icon type={Icons.ErrorTriangle} width={124} height={124} />
							</div>
							<div className='AMLCheckResult_content_error_message'>
								<div className='AMLCheckResult_content_error_message_title'>{t('Error')}</div>
								<div className='AMLCheckResult_content_error_message_text'>
									{t('Something went wrong while checking the address. Please try again or come back later.')}
								</div>
							</div>
							<Button
								className='AMLCheckResult_content_error_btn'
								styleType='green-dark'
								iconBefore={'ARROW_CLOCKWISE'}
								onClick={onTryAgainClick}
							>
								{t('Try again')}
							</Button>
						</div>
					)}

					{aml?.status === 'Canceled' && (
						<div className='AMLCheckResult_content'>
							<div className='AMLCheckResult_content_error_icon'>
								<Icon type={Icons.ErrorTriangle} width={124} height={124} />
							</div>
							<div className='AMLCheckResult_content_error_message'>
								<div className='AMLCheckResult_content_error_message_title'>{t('Address already checked')}</div>
								<div className='AMLCheckResult_content_error_message_text'>
									{t(
										'The AML check request was canceled because this address already has a valid and up-to-date AML result.'
									)}
								</div>
							</div>
						</div>
					)}
				</StickyPanel>
			</div>
		);
	}

	return (
		<div className='AMLCheckResult' style={{ '--colorVariable': indicatorColor }}>
			<StickyPanel
				controls={
					<Button onClick={onGotItClick} styleType='white'>
						{t('Got it')}
					</Button>
				}
			>
				<div className='AMLCheckResult_background' />
				<PageHeader collapsed={true} />

				<div className='AMLCheckResult_content'>
					{isLoadingAML ? (
						<div className='AMLCheckResult_content_spinner'>
							<Lottie animationData={spinner} />
						</div>
					) : (
						<div className='AMLCheckResult_content_fire'>
							{currentRisk === 'Unverified' ? (
								<div className='AMLCheckResult_content_fire_unverified'>
									<IconAML iconOnly size={156} />
								</div>
							) : (
								<Lottie animationData={animationVariant} />
							)}
						</div>
					)}
					<AMLRiskIndicator
						percent={displayPercent}
						colorVariable={indicatorColor}
						isUnverified={currentRisk === 'Unverified'}
					/>
					<ListItem
						className='AMLCheckResult_content_address'
						title={aml?.address || expectedAddress}
						icon={blockchainData?.Icon}
						iconSize='small'
					/>
					{isLoadingAML ? (
						<InlineNotice className='AMLCheckResult_content_notice' type='success' icon={'CLOCK'}>
							<p className='AMLCheckResult_content_notice_title'>{t('AML check is in progress.')}</p>
							<p className='AMLCheckResult_content_notice_text'>
								{t(
									'This may take some time. You can continue using the wallet as usual.\nYou will be notified once the check is complete.'
								)}
							</p>
						</InlineNotice>
					) : (
						<div className='AMLCheckResult_content_details'>
							<div className='AMLCheckResult_content_details_table'>
								<ListItem
									className='AMLCheckResult_content_details_table_row AMLCheckResult_content_details_table_row_colorText'
									title={`${intl.formatMessage({ id: 'AML Risk' })}: ${
										currentRisk === 'Unverified'
											? intl.formatMessage({ id: 'Unverified' })
											: intl.formatMessage({ id: currentRisk })
									}`}
									rightTitle={aml?.riskScore ? `${aml?.riskScore}%` : '-'}
									bottomSeparator={true}
								/>
								<ListItem
									className='AMLCheckResult_content_details_table_row'
									title={t('Last check')}
									rightTitle={formatAmlDate(aml?.checkDate)}
								/>
							</div>
							<ListItem
								className='AMLCheckResult_content_details_btn'
								title={t('More details')}
								icon={'DefaultAML'}
								iconSize='medium'
								trailing={[<Icon type={Icons.CHEVRON} width={8} height={44} />]}
								glare={true}
								onSelect={onMoreDetailsClick}
							/>
						</div>
					)}
				</div>
			</StickyPanel>
		</div>
	);
};

export default AMLCheckResult;
